import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Icon, Label, Segment, Divider, Message } from 'semantic-ui-react';

import ReadOnlyInspectionForm from './ReadOnlyInspectionForm';
import styles from './ViewResponsesModal.module.scss';

const ViewResponsesModal = React.memo(({ responses, formName, onClose }) => {
  const [selectedResponse, setSelectedResponse] = useState(responses[0] || null);

  // Legacy render for responses without schema
  const renderFieldValue = value => {
    // Si es imagen base64
    if (typeof value === 'string' && value.startsWith('data:image/')) {
      return (
        <div className={styles.imagePreview}>
          <img src={value} alt="" />
        </div>
      );
    }

    // Si es array o objeto, buscar imágenes recursivamente
    if (typeof value === 'object' && value !== null) {
      // Función recursiva para extraer todas las imágenes
      const extractImages = obj => {
        const images = [];

        const traverse = item => {
          if (typeof item === 'string' && item.startsWith('data:image/')) {
            images.push(item);
          } else if (Array.isArray(item)) {
            item.forEach(traverse);
          } else if (typeof item === 'object' && item !== null) {
            Object.values(item).forEach(traverse);
          }
        };

        traverse(obj);
        return images;
      };

      const images = extractImages(value);

      if (images.length > 0) {
        return (
          <div className={styles.imagesGrid}>
            {images.map((img, idx) => (
              <div key={idx} className={styles.imagePreview}>
                <img src={img} alt="" />
              </div>
            ))}
          </div>
        );
      }

      // Si no hay imágenes, mostrar como JSON solo si es pequeño
      const jsonString = JSON.stringify(value, null, 2);
      if (jsonString.length < 200) {
        return <pre>{jsonString}</pre>;
      }

      // Objeto grande sin imágenes
      return <span>[Complex object data]</span>;
    }

    // Valor simple
    return <span>{String(value)}</span>;
  };

  return (
    <Modal open onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="history" />
        Response History: {formName}
      </Modal.Header>
      <Modal.Content scrolling>
        <div className={styles.container}>
          {/* Lista de respuestas en sidebar */}
          <div className={styles.sidebar}>
            <h4>Submissions ({responses.length})</h4>
            {responses.map(response => (
              <div
                key={response.id}
                className={`${styles.responseItem} ${
                  selectedResponse?.id === response.id ? styles.active : ''
                }`}
                onClick={() => setSelectedResponse(response)}
              >
                <div className={styles.responseDate}>
                  {new Date(response.submittedAt).toLocaleDateString()}
                </div>
                <div className={styles.responseTime}>
                  {new Date(response.submittedAt).toLocaleTimeString()}
                </div>
                <div className={styles.responseBy}>
                  By: {response.submittedByName || 'External user'}
                </div>
                {response.triggeredCardMove && (
                  <Label size="tiny" color="orange">
                    <Icon name="arrow right" />
                    Auto-moved
                  </Label>
                )}
              </div>
            ))}
          </div>

          {/* Detalle de respuesta seleccionada */}
          <div className={styles.content}>
            {selectedResponse ? (
              <>
                <Segment attached="top">
                  <div className={styles.responseHeader}>
                    <div>
                      <h3>Submitted {new Date(selectedResponse.submittedAt).toLocaleString()}</h3>
                      <p>
                        <Icon name="user" />
                        {selectedResponse.submittedByName || 'External user'}
                      </p>
                    </div>
                    {selectedResponse.triggeredCardMove && selectedResponse.movedToListId && (
                      <Label color="orange">
                        <Icon name="arrow right" />
                        Automatically moved card
                      </Label>
                    )}
                  </div>
                </Segment>

                <Segment attached="bottom" className={styles.responseBody}>
                  {selectedResponse.formId?.formSchema ? (
                    // Renderizado Fiel (con esquema)
                    <ReadOnlyInspectionForm
                      formSchema={selectedResponse.formId.formSchema}
                      responseData={selectedResponse.responseData || {}}
                      signatureData={selectedResponse.signatureData}
                    />
                  ) : (
                    // Renderizado Legacy (sin esquema)
                    <div className={styles.responseData}>
                      <Message info>
                        <Icon name="info circle" />
                        This is an old response format.
                      </Message>
                      <h4>Response Data:</h4>
                      {Object.entries(selectedResponse.responseData || {}).map(([key, value]) => (
                        <div key={key} className={styles.field}>
                          <strong>{key}:</strong>
                          <div className={styles.fieldValue}>{renderFieldValue(value)}</div>
                        </div>
                      ))}
                      {/* Legacy global signature */}
                      {selectedResponse.signatureData &&
                        typeof selectedResponse.signatureData === 'string' && (
                          <>
                            <Divider />
                            <div className={styles.signature}>
                              <h4>Digital Signature:</h4>
                              <img src={selectedResponse.signatureData} alt="Signature" />
                            </div>
                          </>
                        )}
                    </div>
                  )}
                </Segment>
              </>
            ) : (
              <div className={styles.emptyState}>
                <Icon name="file alternate outline" size="huge" />
                <p>Select a submission to view details</p>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
        {selectedResponse && (
          <Button primary disabled>
            <Icon name="download" />
            Download PDF (Coming Soon)
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
});

ViewResponsesModal.propTypes = {
  responses: PropTypes.array.isRequired,
  formName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ViewResponsesModal;
